import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Ticket, Eye, EyeOff, CreditCard, Crown, Zap, Palette } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { PurchaseCreditsModal } from '../components/PurchaseCreditsModal';
import { KuponTemplateSelector, KuponTemplatePreview } from '../components/KuponTemplateSelector';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BusinessKuponsPage = () => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [searchParams, setSearchParams] = useSearchParams();
  const [kupons, setKupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creditsModalOpen, setCreditsModalOpen] = useState(false);
  const [tierInfo, setTierInfo] = useState(null);
  const [ownedTemplates, setOwnedTemplates] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    originalPrice: '',
    discountedPrice: '',
    category: '',
    duration: '1',
    stock: '',
    imageUrl: '',
    template: 'classic'
  });

  useEffect(() => {
    fetchKupons();
    fetchTierInfo();
    
    // Auto-open create dialog if action=create in URL
    if (searchParams.get('action') === 'create') {
      setDialogOpen(true);
      // Clear the URL param after opening
      setSearchParams({});
    }
  }, []);

  const fetchKupons = async () => {
    try {
      const response = await axios.get(`${API}/business/${user.id}/kupons`);
      setKupons(response.data);
    } catch (error) {
      console.error('Error fetching kupons:', error);
      toast.error('Error al kargar kupones');
    } finally {
      setLoading(false);
    }
  };

  const fetchTierInfo = async () => {
    try {
      const response = await axios.get(`${API}/business/${user.id}/tier-info`);
      setTierInfo(response.data);
    } catch (error) {
      console.error('Error fetching tier info:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${API}/kupons`,
        {
          title: formData.title,
          description: formData.description,
          originalPrice: parseFloat(formData.originalPrice),
          discountedPrice: parseFloat(formData.discountedPrice),
          category: formData.category,
          duration: parseInt(formData.duration),
          stock: parseInt(formData.stock),
          imageUrl: formData.imageUrl || null,
          templateId: formData.template || 'classic'
        },
        {
          headers: {
            'X-Business-Id': user.id
          }
        }
      );
      
      toast.success('¡Kupon kreado exitosamente!');
      setDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        originalPrice: '',
        discountedPrice: '',
        category: '',
        duration: '1',
        stock: '',
        imageUrl: '',
        template: 'classic'
      });
      fetchKupons();
      fetchTierInfo(); // Refresh tier info
    } catch (error) {
      console.error('Error creating kupon:', error);
      const errorMsg = error.response?.data?.detail || 'Error al krear kupon';
      
      // Check if it's a credits/limit error
      if (errorMsg.includes('limit reached')) {
        toast.error('Límite de kupones gratis alcanzado. Kompra kréditos o mejora a Premium.');
        setCreditsModalOpen(true);
      } else if (errorMsg.includes('No credits')) {
        toast.error('No tienes kréditos disponibles. Kompra más kréditos para kontinuar.');
        setCreditsModalOpen(true);
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const toggleKuponStatus = async (kuponId, currentStatus) => {
    if (!currentStatus) return; // Already deactivated
    
    try {
      await axios.patch(`${API}/kupons/${kuponId}/deactivate`);
      toast.success('Kupon desaktivado');
      fetchKupons();
    } catch (error) {
      console.error('Error deactivating kupon:', error);
      toast.error('Error al desactivar kupon');
    }
  };

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-6">
        {/* Credits Widget */}
        {tierInfo && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-gradient-to-br from-local-sage/10 to-local-sage/5 border border-local-sage/20 rounded-2xl p-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Plan Aktual</p>
                <div className="flex items-center gap-2">
                  {tierInfo.tier === 'premium' && <Crown className="w-5 h-5 text-reward-gold" />}
                  {tierInfo.tier === 'credits' && <Zap className="w-5 h-5 text-local-sage" />}
                  <p className="text-xl font-bold capitalize">{tierInfo.tier === 'free' ? 'Gratis' : tierInfo.tier === 'credits' ? 'Kréditos' : 'Premium'}</p>
                </div>
              </div>

              {tierInfo.tier === 'credits' && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Kréditos Disponibles</p>
                  <p className="text-3xl font-bold text-local-sage">{tierInfo.credits}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-muted-foreground mb-1">Kupones Aktivos</p>
                <p className="text-2xl font-bold">
                  {tierInfo.activeKupons}{tierInfo.tier === 'free' ? '/3' : ''}
                </p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Komisión Aktual</p>
                <p className="text-2xl font-bold">{tierInfo.commissionRate ? (tierInfo.commissionRate * 100).toFixed(0) : 0}%</p>
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              {tierInfo.tier !== 'premium' && (
                <Button
                  onClick={() => setCreditsModalOpen(true)}
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  data-testid="buy-credits-button"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Komprar Kréditos
                </Button>
              )}
              {tierInfo.tier !== 'premium' && (
                <Button
                  size="sm"
                  className="bg-reward-gold hover:bg-reward-gold/90 rounded-full"
                  data-testid="upgrade-premium-button"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade a Premium
                </Button>
              )}
            </div>
          </motion.div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-manrope font-bold text-3xl sm:text-4xl mb-2">Mis Kupones</h1>
            <p className="text-muted-foreground">Krea y gestiona tus ofertas</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-local-sage hover:bg-local-sage/90 rounded-full" data-testid="create-kupon-button">
                <Plus className="w-5 h-5 mr-2" />
                Krear Kupon
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-2xl">Nuevo Kupon</DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="title">Título</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Ej: 20% en tu próxima kompra"
                    required
                    className="rounded-xl"
                    data-testid="kupon-title-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Deskripsion</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe tu oferta..."
                    required
                    className="rounded-xl min-h-[100px]"
                    data-testid="kupon-description-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="originalPrice">Precio Original (€)</Label>
                    <Input
                      id="originalPrice"
                      name="originalPrice"
                      type="number"
                      step="0.01"
                      value={formData.originalPrice}
                      onChange={handleChange}
                      placeholder="50.00"
                      required
                      className="rounded-xl"
                      data-testid="kupon-original-price-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="discountedPrice">Precio kon Descuento (€)</Label>
                    <Input
                      id="discountedPrice"
                      name="discountedPrice"
                      type="number"
                      step="0.01"
                      value={formData.discountedPrice}
                      onChange={handleChange}
                      placeholder="40.00"
                      required
                      className="rounded-xl"
                      data-testid="kupon-discounted-price-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="category">Kategoría</Label>
                    <Input
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      placeholder="Restaurante"
                      required
                      className="rounded-xl"
                      data-testid="kupon-category-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="duration">Duración (días)</Label>
                    <Input
                      id="duration"
                      name="duration"
                      type="number"
                      min="1"
                      max="3"
                      value={formData.duration}
                      onChange={handleChange}
                      required
                      className="rounded-xl"
                      data-testid="kupon-duration-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="stock">Stock</Label>
                    <Input
                      id="stock"
                      name="stock"
                      type="number"
                      min="1"
                      value={formData.stock}
                      onChange={handleChange}
                      placeholder="100"
                      required
                      className="rounded-xl"
                      data-testid="kupon-stock-input"
                    />
                  </div>
                </div>
                
                {/* Template Selector */}
                <KuponTemplateSelector
                  selectedTemplate={formData.template}
                  onSelect={(template) => setFormData({ ...formData, template })}
                  ownedTemplates={user?.owned_templates || []}
                  hasUnlimitedAccess={!!user?.unlimited_templates_until}
                />

                {/* Template Preview */}
                {formData.title && (
                  <div>
                    <Label className="text-sm mb-2 block">Vista Previa</Label>
                    <KuponTemplatePreview 
                      template={formData.template} 
                      kuponData={formData}
                    />
                  </div>
                )}
                
                <div>
                  <Label htmlFor="imageUrl">URL de Imagen (opcional)</Label>
                  <Input
                    id="imageUrl"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={handleChange}
                    placeholder="https://..."
                    className="rounded-xl"
                    data-testid="kupon-image-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-local-sage hover:bg-local-sage/90 rounded-full py-6"
                  data-testid="submit-kupon-button"
                >
                  Krear Kupon
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Kupons List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-local-sage" />
          </div>
        ) : kupons.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="business-kupons-grid">
            {kupons.map((kupon) => {
              const discount = Math.round(((kupon.originalPrice - kupon.discountedPrice) / kupon.originalPrice) * 100);
              const expiresAt = new Date(kupon.expiresAt);
              const isExpired = expiresAt < new Date();
              
              return (
                <motion.div
                  key={kupon.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card rounded-2xl overflow-hidden shadow-card border border-border/50"
                >
                  <div className="relative h-48 bg-gradient-to-br from-local-sage to-local-sage/60">
                    {kupon.imageUrl ? (
                      <img src={kupon.imageUrl} alt={kupon.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Ticket className="w-16 h-16 text-white/50" />
                      </div>
                    )}
                    <div className="absolute top-4 right-4 bg-reward-gold text-white px-4 py-2 rounded-full font-bold text-sm">
                      -{discount}%
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="font-manrope font-semibold text-lg mb-2 line-clamp-2">{kupon.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{kupon.description}</p>
                    
                    <div className="flex items-end gap-2 mb-4">
                      <span className="text-2xl font-bold text-local-sage">{kupon.discountedPrice.toFixed(2)}€</span>
                      <span className="text-sm text-muted-foreground line-through mb-1">{kupon.originalPrice.toFixed(2)}€</span>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                      <span>Stock: {kupon.stock}</span>
                      <span>{isExpired ? 'Expirado' : `Expira: ${expiresAt.toLocaleDateString('es-ES')}`}</span>
                    </div>
                    
                    <Button
                      variant={kupon.isActive && !isExpired ? 'outline' : 'default'}
                      size="sm"
                      onClick={() => toggleKuponStatus(kupon.id, kupon.isActive)}
                      className="w-full rounded-full"
                      disabled={!kupon.isActive || isExpired}
                      data-testid="toggle-kupon-button"
                    >
                      {kupon.isActive && !isExpired ? (
                        <><EyeOff className="w-4 h-4 mr-2" /> Desactivar</>
                      ) : (
                        <>{isExpired ? 'Expirado' : 'Inaktivo'}</>
                      )}
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20">
            <Ticket className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-6">Aún no has kreado ningún kupon</p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-local-sage hover:bg-local-sage/90 rounded-full"
            >
              <Plus className="w-5 h-5 mr-2" />
              Krear tu primer Kupon
            </Button>
          </div>
        )}
      </div>

      {/* Purchase Credits Modal */}
      <PurchaseCreditsModal 
        isOpen={creditsModalOpen}
        onClose={() => setCreditsModalOpen(false)}
        onPurchaseSuccess={() => {
          fetchTierInfo();
          toast.success('¡Kréditos komprados exitosamente!');
        }}
      />
    </div>
  );
};